import React, { useState, useEffect, useMemo } from 'react';
import { createTheme, ThemeProvider, Tooltip, Typography, Box, Button, Stack } from '@mui/material';
import { DataGrid, GridColDef, GridRowParams, GridSortModel } from '@mui/x-data-grid';
import { ArrowDropDown, ArrowDropUp, ConfirmationNumber as ConfirmationNumberIcon, KeyboardArrowLeft, KeyboardArrowRight } from '@mui/icons-material';
import styles from '../../../../assets/css/components/Lobby.module.css';
import playersIcon from '/images/lobby/players.svg';
import freerollIcon from '/images/lobby/freeroll.svg';
import satelliteIcon from '/images/lobby/satellite.svg';
import CircleIcon from '@mui/icons-material/Circle';
import { IUserState } from '../../../../Core/State/IUserState';
import { InteractionHandler } from '../../../../Core/Handler/InteractionHandler';
import DetachedTournamentLobby from './DetachedTournamentLobby';
import { ITournamentState } from '../../../../Core/State/ILobbyTournamentState';
import TournamentLobbyContent from './TournamentLobbyContent';
import TicketPrizesTooltip from './TicketPrizesTooltip';
import defaultTourneyImg from '/images/tournament/default.png';
import { t } from '../../../../i18n';
import { useTranslation } from 'react-i18next';
import {
    buildTournamentLobbyLiveRowsRequest,
    buildTournamentLobbyDetailsRequest,
    buildTournamentScheduleFilters,
    formatTournamentScheduleGlobalSegmentLabel,
    formatTournamentScheduleSegmentLabel,
    getDefaultTournamentScheduleFilterState,
    getTournamentScheduleCurrentSegmentStartIndex,
    getTournamentScheduleSegmentFromStartIndex,
    getTournamentLobbyDetailsTournament,
    getTournamentLobbyLiveRowsForRequest,
    isTournamentScheduleGlobalView,
    isTournamentScheduleCurrentTimeAnchoredPreset,
    isTournamentScheduleDateToday,
    logTournamentLobbyTournamentDebug,
    mergeScheduleTournamentsWithLobbyLiveRows,
    mergeScheduleTournamentsWithRealtime,
    shiftTournamentScheduleDate,
    TOURNAMENT_SCHEDULE_UI_SEGMENT_SIZE,
    useLobbyTournamentSchedule,
    useTournamentLobbyDetails,
    useTournamentLobbyLiveRows,
    type TournamentScheduleFilterState
} from '../../../../Tournament/TournamentSchedule';
import TournamentScheduleFilterBar from './TournamentScheduleFilterBar';
import {
    formatCurrency,
    formatTimeDifference,
    isLateRegistrationOpen,
    getLateRegistrationRemaining,
    getEffectivePrize,
    getRegistrationTimes,
    getEligibleTickets,
    canRegisterForTournament,
    getTournamentRemainingPlayers,
    getTournamentRoomActivePlayerCount,
    getTournamentTotalEntrants,
    isTerminalTournamentStatus,
    isTournamentRegisteredByUser,
    shouldShowTournamentRemainingCount
} from '../../../../Utils/TournamentUtils';

interface DesktopTournamentLobbyProps {
    lobbyState: any;
    userState: IUserState;
    interactionHandler: InteractionHandler;
}

const DEFAULT_TOURNAMENT_SORT_MODEL: GridSortModel = [{ field: 'startTime', sort: 'asc' }];
const SORTABLE_TOURNAMENT_FIELDS = new Set([
    'startTime',
    'name',
    'type',
    'buyIn',
    'registeredPlayerCount',
    'status',
    'prizePool',
]);

type TournamentSortDirection = 'asc' | 'desc';

const getActiveTournamentSort = (sortModel: GridSortModel): { field: string; direction: TournamentSortDirection } => {
    const sort = sortModel[0];
    if (
        sort &&
        SORTABLE_TOURNAMENT_FIELDS.has(sort.field) &&
        (sort.sort === 'asc' || sort.sort === 'desc')
    ) {
        return { field: sort.field, direction: sort.sort };
    }

    return { field: 'startTime', direction: 'asc' };
};

const isDefaultTournamentSort = (sortModel: GridSortModel): boolean => {
    const sort = getActiveTournamentSort(sortModel);
    return sort.field === 'startTime' && sort.direction === 'asc';
};

const compareTournamentText = (a: unknown, b: unknown): number => (
    String(a ?? '').localeCompare(String(b ?? ''), undefined, {
        numeric: true,
        sensitivity: 'base',
    })
);

const getTournamentStartTimeValue = (tournament: ITournamentState): number => {
    const startMs = new Date(tournament.startTime).getTime();
    return Number.isFinite(startMs) ? startMs : 0;
};

const getTournamentBuyInSortValue = (tournament: ITournamentState): number => (
    (tournament.buyIn || 0) + (tournament.entryFee || 0)
);

const getTournamentPlayersSortValue = (tournament: ITournamentState): number => (
    shouldShowTournamentRemainingCount(tournament)
        ? getTournamentRemainingPlayers(tournament)
        : getTournamentTotalEntrants(tournament)
);

const getTournamentStatusSortValue = (
    tournament: ITournamentState,
    userId: string | undefined,
    now: Date
): number => {
    if (isTerminalTournamentStatus(tournament.status)) {
        return tournament.status === 'completed' ? 7 : 8;
    }

    if (isTournamentRegisteredByUser(tournament, userId) && tournament.status !== 'running' && tournament.status !== 'paused') {
        return 0;
    }

    if (isLateRegistrationOpen(tournament, now)) return 1;

    const order: Record<string, number> = {
        registration: 2,
        scheduled: 2,
        starting: 3,
        running: 4,
        paused: 5,
        late_registration: 1,
    };

    return order[tournament.status] ?? 6;
};

const compareTournamentRowsForSort = (
    a: ITournamentState,
    b: ITournamentState,
    field: string,
    now: Date,
    userId: string | undefined
): number => {
    switch (field) {
        case 'name':
            return compareTournamentText(a.name, b.name);
        case 'type':
            return compareTournamentText(a.type, b.type);
        case 'buyIn':
            return getTournamentBuyInSortValue(a) - getTournamentBuyInSortValue(b);
        case 'registeredPlayerCount':
            return getTournamentPlayersSortValue(a) - getTournamentPlayersSortValue(b);
        case 'status':
            return getTournamentStatusSortValue(a, userId, now) - getTournamentStatusSortValue(b, userId, now);
        case 'prizePool':
            return getEffectivePrize(a) - getEffectivePrize(b);
        case 'startTime':
        default:
            return getTournamentStartTimeValue(a) - getTournamentStartTimeValue(b);
    }
};

const sortTournamentLobbyRows = (
    tournaments: ITournamentState[],
    sortModel: GridSortModel,
    now: Date,
    userId: string | undefined
): ITournamentState[] => {
    const { field, direction } = getActiveTournamentSort(sortModel);
    const directionMultiplier = direction === 'asc' ? 1 : -1;

    return [...tournaments].sort((a, b) => {
        const primary = compareTournamentRowsForSort(a, b, field, now, userId);
        if (primary !== 0) return primary * directionMultiplier;

        const startTime = getTournamentStartTimeValue(a) - getTournamentStartTimeValue(b);
        if (startTime !== 0) return startTime;

        return compareTournamentText(a.name, b.name);
    });
};

const DesktopTournamentLobby: React.FC<DesktopTournamentLobbyProps> = ({ userState, interactionHandler }) => {

    const [selectedTournament, setSelectedTournament] = useState<ITournamentState | null>(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [modalTournamentId, setModalTournamentId] = useState<string | null>(null);
    const [currentTime, setCurrentTime] = useState(new Date());
    const [actionCooldown, setActionCooldown] = useState(false);
    const [scheduleFilters, setScheduleFilters] = useState<TournamentScheduleFilterState>(() => getDefaultTournamentScheduleFilterState());
    const [scheduleSegmentStartIndex, setScheduleSegmentStartIndex] = useState<number | null>(null);
    const [tournamentSortModel, setTournamentSortModel] = useState<GridSortModel>(DEFAULT_TOURNAMENT_SORT_MODEL);
    const { i18n } = useTranslation();
    const scheduleQueryFilters = useMemo(() => buildTournamentScheduleFilters(scheduleFilters), [scheduleFilters]);
    const isScheduleGlobalPagination = isTournamentScheduleGlobalView(scheduleFilters);
    const scheduleQuery = useLobbyTournamentSchedule({ filters: scheduleQueryFilters });

    const darkTheme = useMemo(() => createTheme({
        palette: {
            mode: "dark",
            primary: {
                main: "#F93751"
            },
            secondary: {
                main: "#3E3F49",
                dark: "#35363e"
            }
        },
        typography: {
            fontFamily: 'Inter, serif',
        },
    }), []);

    const isLoading = scheduleQuery.isLoading;

    // Update current time every second for real-time calculations
    useEffect(() => {
        const timer = setInterval(() => {
            setCurrentTime(new Date());
        }, 1000);
        
        return () => clearInterval(timer);
    }, []);

    const handleRowClick = (params: GridRowParams<ITournamentState>) => {
        setSelectedTournament(params.row);
    };

    const handleRowDoubleClick = (params: GridRowParams<ITournamentState>) => {
        setModalTournamentId(params.row.gameId);
        setModalOpen(true);
    };

    const handleTournamentLobbyButton = (gameId: string) => {
        setModalTournamentId(gameId);
        setModalOpen(true);
    }

    const resetScheduleSegmentForSort = (sortModel: GridSortModel) => {
        setScheduleSegmentStartIndex(isDefaultTournamentSort(sortModel) ? null : 0);
    };

    const handleScheduleFilterChange = (nextFilters: TournamentScheduleFilterState) => {
        const nextSortModel = nextFilters.preset === 'CUSTOM'
            ? tournamentSortModel
            : DEFAULT_TOURNAMENT_SORT_MODEL;

        setScheduleFilters(nextFilters);
        if (nextSortModel !== tournamentSortModel) {
            setTournamentSortModel(nextSortModel);
        }
        resetScheduleSegmentForSort(nextSortModel);
    };

    const resetScheduleFilters = () => {
        setScheduleFilters(getDefaultTournamentScheduleFilterState());
        setTournamentSortModel(DEFAULT_TOURNAMENT_SORT_MODEL);
        resetScheduleSegmentForSort(DEFAULT_TOURNAMENT_SORT_MODEL);
    };

    const handleTournamentSortModelChange = (nextSortModel: GridSortModel) => {
        const normalizedSortModel = nextSortModel.length > 0 ? nextSortModel : DEFAULT_TOURNAMENT_SORT_MODEL;
        setTournamentSortModel(normalizedSortModel);
        resetScheduleSegmentForSort(normalizedSortModel);
    };

    const clearSchedulePresetHighlight = () => {
        setScheduleFilters((prev) => (
            prev.preset === 'CUSTOM' ? prev : { ...prev, preset: 'CUSTOM' }
        ));
    };

    const shiftScheduleDay = (days: number) => {
        setScheduleSegmentStartIndex(days < 0 ? Number.MAX_SAFE_INTEGER : 0);
        setScheduleFilters((prev) => ({
            ...prev,
            date: shiftTournamentScheduleDate(prev.date, days),
            preset: 'CUSTOM'
        }));
    };

    const handlePreviousSchedulePage = () => {
        if (schedulePage.startIndex > 0) {
            setScheduleSegmentStartIndex(Math.max(0, schedulePage.startIndex - TOURNAMENT_SCHEDULE_UI_SEGMENT_SIZE));
            if (!isScheduleGlobalPagination) clearSchedulePresetHighlight();
            return;
        }

        if (isScheduleGlobalPagination) return;
        shiftScheduleDay(-1);
    };

    const handleNextSchedulePage = () => {
        if (schedulePage.endItemNumber < schedulePage.totalItems) {
            setScheduleSegmentStartIndex(schedulePage.endItemNumber);
            if (!isScheduleGlobalPagination) clearSchedulePresetHighlight();
            return;
        }

        if (isScheduleGlobalPagination) return;
        shiftScheduleDay(1);
    };

    const handleModalClose = () => {
        setModalOpen(false);
        setModalTournamentId(null);
    };

    const formatCurrencyLocal = (amount: number) => {
        return formatCurrency(amount);
    };

    const isUserRegistered = isTournamentRegisteredByUser(selectedTournament, userState.id);

    const handleRegisterClick = () => {
        if (selectedTournament && !actionCooldown) {
            setActionCooldown(true);
            interactionHandler.signUpForTournament(selectedTournament.gameId);
            setTimeout(() => setActionCooldown(false), 2000);
        }
    };

    const handleUnregisterClick = () => {
        if (selectedTournament && !actionCooldown) {
            setActionCooldown(true);
            interactionHandler.unsignFromTournament(selectedTournament.gameId);
            setTimeout(() => setActionCooldown(false), 2000);
        }
    };

    const handleRegisterWithTicket = (ticketId: string, tournamentId: string) => {
        if (!actionCooldown) {
            setActionCooldown(true);
            interactionHandler.registerForTournamentWithTicket(tournamentId, ticketId);
            setTimeout(() => setActionCooldown(false), 2000);
        }
    };

    // Format time difference for registration periods
    const formatTimeDifferenceLocal = (targetTime: Date, currentTime: Date = new Date()): string => {
        return formatTimeDifference(targetTime, currentTime);
    };

    // Late registration helpers (server-driven with client-side fallback)
    const isLateRegistrationOpenLocal = (t: ITournamentState, now: Date = new Date()): boolean => {
        return isLateRegistrationOpen(t, now);
    };

    const getLateRegistrationRemainingLocal = (t: ITournamentState, now: Date = new Date()): number => {
        return getLateRegistrationRemaining(t, now);
    };

    const getEffectivePrizeLocal = (tournament: ITournamentState): number => {
        return getEffectivePrize(tournament);
    };

    // Calculate registration window times (ITournamentState)
    const getRegistrationTimesLocal = (tournament: ITournamentState) => {
        return getRegistrationTimes(tournament);
    };

    // Format date for display in rankings table
    // formatDate unused

    // Format relative time for display in rankings table
    // formatRelativeTime unused

    const columns: GridColDef<ITournamentState>[] = useMemo(() => [
        {
            field: 'startTime', flex: 1.5, headerName: t('Tournament:columns.start'), headerClassName: styles.tableHeader, type: 'dateTime', resizable: false, valueGetter: (params) => new Date(params.value as string),
            renderCell: (params: any) => {
                const row = params.row as ITournamentState;
                const date = params.value as Date;
                if (!date || isNaN(date.getTime())) return '';

                const now = new Date();
                const timeDiffMs = date.getTime() - now.getTime();

                if (row.status == 'paused') {
                    return (<span style={{ color: "rgb(255,152,0)" }}>{t('Tournament:status.onBreak')}</span>);
                }

                if (row.status == 'completed' || row.status == 'cancelled') {
                    const statusLabel = row.status === 'completed' ? t('Tournament:status.completed') : t('Tournament:status.cancelled');
                    return (<span style={{ color: "rgba(255,255,255,0.5)", textTransform: "capitalize" }}>{statusLabel}</span>);
                }

                // Within 5 minutes - show seconds only
                if (timeDiffMs > 0 && timeDiffMs <= 5 * 60 * 1000) {
                    const totalSeconds = Math.floor(timeDiffMs / 1000);
                    return (<span style={{ color: "#FFC100" }}>{t('Tournament:startText.startsIn')} {totalSeconds}s</span>);
                }

                // Within 30 minutes - show minutes only
                if (timeDiffMs > 0 && timeDiffMs <= 30 * 60 * 1000) {
                    const totalMinutes = Math.floor(timeDiffMs / (60 * 1000));
                    return (<span style={{ color: "#1BE958" }}>{t('Tournament:startText.startsIn')} {totalMinutes}min</span>);
                }

                // Late registration (computed) with time remaining
                if (isLateRegistrationOpenLocal(row, now)) {
                    const remainingMs = getLateRegistrationRemainingLocal(row, now);

                    // Under 5 minutes - show seconds only
                    if (remainingMs <= 5 * 60 * 1000) {
                        const totalSeconds = Math.floor(remainingMs / 1000);
                        return (<span style={{ color: "#F93751" }}>{t('Tournament:startText.lateReg')} {totalSeconds}s</span>);
                    }

                    // Over 5 minutes - show minutes only
                    const totalMinutes = Math.floor(remainingMs / (60 * 1000));
                    return (<span style={{ color: "#F93751" }}>{t('Tournament:startText.lateReg')} {totalMinutes}min</span>);
                }

                if (row.status == 'running') {
                    return (<span style={{ color: "#6BD2FF" }}>{t('Tournament:status.running')}</span>);
                }

                // Check if the date is today
                const isToday =
                    date.getDate() === now.getDate() &&
                    date.getMonth() === now.getMonth() &&
                    date.getFullYear() === now.getFullYear();

                const hours = date.getHours().toString().padStart(2, '0');
                const minutes = date.getMinutes().toString().padStart(2, '0');

                if (isToday) {
                    return (<span style={{ color: "#FFFFFF" }}>{t('Tournament:startText.today')} {hours}:{minutes}</span>);
                }

                const day = date.getDate();
                const month = date.toLocaleString('en-US', { month: 'short' });
                return `${day} ${month}, ${hours}:${minutes}`;
            }
        },
        {
            field: 'name', flex: 3, headerName: t('Tournament:columns.name'), headerClassName: styles.tableHeader, type: 'string', resizable: false, renderCell: (params) => {
                const { buyIn, name, imageName, isSatellite } = params.row;
                const imageSrc = imageName 
                    ? `${import.meta.env.BASE_URL}images/tournament/${imageName}` 
                    : defaultTourneyImg;
                
                return (
                    <div style={{ display: 'flex', width: '100%', paddingRight: '20px', alignItems: 'center' }}>
                        <img 
                            src={imageSrc} 
                            alt="tournament logo" 
                            width={65} 
                            style={{ maxHeight: '32px', objectFit: 'contain' }} 
                            onError={(e) => {
                                e.currentTarget.src = defaultTourneyImg;
                            }}
                        />
                        <span style={{ marginLeft: '1rem', marginRight: 'auto', whiteSpace: 'normal', lineHeight: 'normal' }}>{name}</span>
                        {isSatellite && (
                            <Tooltip title={t('Tournament:tooltips.satellite')} placement="top" arrow>
                                <img src={satelliteIcon} alt="Satellite Icon" style={{ marginLeft: 5 }} />
                            </Tooltip>
                        )}
                        {buyIn === 0 && (
                            <Tooltip title={t('Tournament:tooltips.freeroll')} placement="top" arrow>
                                <img src={freerollIcon} alt="Freeroll Icon" style={{ marginLeft: 5 }} />
                            </Tooltip>
                        )}
                    </div>
                );
            }
        },
        {
            field: 'type', flex: 1, headerName: t('Tournament:columns.game'), headerClassName: styles.tableHeader, type: 'string', resizable: false,
            valueGetter: (params: any) => {
                switch (params.value.toLowerCase()) {
                    case 'texas':
                        return t('Tournament:gameTypes.holdem');
                    case 'aof':
                        return t('Tournament:gameTypes.aof');
                    default:
                        return params.value.charAt(0).toUpperCase() + params.value.slice(1);
                }
            },
            renderCell: (params) => {
                const value = params.value?.toLowerCase();

                switch (value) {
                    case 'texas':
                        return (
                            <Box className={styles.typeLabel} sx={{ backgroundColor: '#2B5056', color: '#4DDAA2' }}>
                                {params.value}
                            </Box>
                        );
                    case 'aof':
                        return (
                            <Box className={styles.typeLabel} sx={{ backgroundColor: '#57222E', color: '#FC5468' }}>
                                {params.value}
                            </Box>
                        );
                    default:
                        return (
                            <Box className={styles.typeLabel} sx={{ backgroundColor: '#2B5056', color: '#4DDAA2' }}>
                                {params.value}
                            </Box>
                        );
                }
            }
        },
        {
            field: 'buyIn', flex: 1, headerName: t('Tournament:columns.buyIn'), headerClassName: styles.tableHeader, type: 'string', resizable: false, valueGetter: (params) => {
                const { buyIn, entryFee } = params.row;
                const formatNumber = (num?: number | null) => {
                    if (num === null || num === undefined || Number.isNaN(num)) {
                        return '0';
                    }
                    return Number.isInteger(num) ? num.toString() : num.toFixed(2);
                };
                if (!buyIn) {
                    return t('Tournament:gameTypes.freeroll');
                }
                return `$${formatNumber(buyIn)} + $${formatNumber(entryFee)}`;
            }
        },
        {
            field: 'registeredPlayerCount', flex: 1, headerName: t('Tournament:columns.players'), headerClassName: styles.tableHeader, type: 'number', resizable: false, align: 'left', headerAlign: 'left', renderCell: (params) => {
                const tournament = params.row as ITournamentState;
                const totalEntrants = getTournamentTotalEntrants(tournament);
                const remainingPlayers = getTournamentRemainingPlayers(tournament);
                const showRemaining = shouldShowTournamentRemainingCount(tournament);
                const tooltipText = showRemaining
                    ? `${remainingPlayers} out of ${totalEntrants} players remain in the tournament.`
                    : `${totalEntrants} players registered for this tournament.`;
                return (
                    <Tooltip title={tooltipText} placement="top" arrow>
                        <Box className={styles.playerCount} sx={{ '--color': '#ffffff75' }}>
                            <img src={playersIcon} alt="Players Icon" style={{ width: 15, height: 13, marginRight: 4 }} />
                            {showRemaining ? `${remainingPlayers} / ${totalEntrants}` : totalEntrants}
                        </Box>
                    </Tooltip>
                )
            }
        },
        {
            field: 'status',
            flex: 1.2,
            headerName: t('Tournament:columns.state'),
            headerClassName: styles.tableHeader,
            type: 'string',
            resizable: false,
            renderCell: (params: any) => {
                const stateColors: Record<string, string> = {
                    'running': '#6BD2FF',
                    'registration': '#1BE958',
                    'late_registration': '#F93751',
                    'scheduled': '#1BE958',
                    'starting': '#1BE958',
                    'completed': '#16EFC1',
                    'cancelled': '#F93751',
                    'paused': '#FF9800',
                };
                const statusText = params.value as string;
                const row = params.row as ITournamentState;
                const isLate = isLateRegistrationOpenLocal(row, new Date());
                
                const isUserRegistered = isTournamentRegisteredByUser(row, userState.id);
                
                let displayText: string;
                let color: string;
                
                if (isTerminalTournamentStatus(row.status)) {
                    displayText = row.status === 'completed' ? t('Tournament:status.completed') : t('Tournament:status.cancelled');
                    color = stateColors[row.status] || '#FFFFFF';
                } else if (isUserRegistered && (row.status !== 'running' && row.status !== 'paused')) {
                    displayText = t('Tournament:box.registered');
                    color = '#6BD2FF';
                } else if (row.status === 'paused') {
                    displayText = t('Tournament:status.onBreak');
                    color = '#FF9800';
                } else if (isLate) {
                    displayText = t('Tournament:status.lateRegistration');
                    color = '#F93751';
                } else {
                    // Translate status
                    const statusTranslations: Record<string, string> = {
                        'running': t('Tournament:status.running'),
                        'registration': t('Tournament:status.registration'),
                        'late_registration': t('Tournament:status.lateRegistration'),
                        'scheduled': t('Tournament:status.registration'),
                        'starting': t('Tournament:status.registering'),
                        'completed': t('Tournament:status.completed'),
                        'cancelled': t('Tournament:status.cancelled'),
                        'paused': t('Tournament:status.onBreak'),
                    };
                    displayText = statusTranslations[statusText] || statusText;
                    color = stateColors[statusText] || '#FFFFFF';
                }
                
                return (
                    <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span
                            style={{
                                color: color,
                                fontWeight: '500',
                                fontSize: '12px',
                                backgroundColor: `${color}33`,
                                borderRadius: '3px',
                                padding: '2px 4px',
                                textTransform: 'capitalize',
                            }}
                        >
                            {displayText}
                        </span>
                    </span>
                );
            }
        },
        {
            field: 'prizePool', flex: 1, headerName: t('Tournament:columns.prize'), headerClassName: styles.tableHeader, cellClassName: styles.lastTableCell, type: 'string', resizable: false,
            valueGetter: (params) => getEffectivePrizeLocal(params.row),
            valueFormatter: (params) => formatCurrencyLocal(params.value as number),
            renderCell: (params) => {
                const tournament = params.row as ITournamentState;
                const hasTickets = tournament.ticketPrizes && tournament.ticketPrizes.length > 0;
                
                return (
                    <span style={{ fontSize: '14px', color: '#FFC100', fontWeight: 'bold' }}>
                        {params.formattedValue}
                        {hasTickets && (
                            <TicketPrizesTooltip ticketPrizes={tournament.ticketPrizes}>
                                <span style={{ cursor: 'help' }}>
                                    {tournament.ticketPrizes!.length === 1 ? ` + ${t('Tournament:labels.ticket')}` : ` + ${t('Tournament:labels.tickets')}`}
                                </span>
                            </TicketPrizesTooltip>
                        )}
                    </span>
                );
            }
        },
    ], [userState.id, i18n.language]); // Include language to re-render columns on language change

    const customDataGridClasses = {
        virtualScroller: styles.customScroller,
        columnHeaders: styles.tableHeaderRow,
        columnHeader: styles.tableHeaderCell,
        cell: styles.tableCell,
        row: styles.tableRow,
    };

    const sortedScheduleTournaments = useMemo(
        () => sortTournamentLobbyRows(scheduleQuery.tournaments, tournamentSortModel, currentTime, userState.id),
        [scheduleQuery.tournaments, tournamentSortModel, currentTime, userState.id]
    );
    const shouldAnchorCurrentSegment = (
        scheduleSegmentStartIndex === null &&
        isDefaultTournamentSort(tournamentSortModel) &&
        isTournamentScheduleCurrentTimeAnchoredPreset(scheduleFilters.preset) &&
        isTournamentScheduleDateToday(scheduleFilters.date, currentTime)
    );
    const currentScheduleStartIndex = shouldAnchorCurrentSegment
        ? getTournamentScheduleCurrentSegmentStartIndex(sortedScheduleTournaments, currentTime)
        : null;
    const activeScheduleStartIndex = currentScheduleStartIndex ?? scheduleSegmentStartIndex ?? 0;
    const schedulePage = useMemo(
        () => getTournamentScheduleSegmentFromStartIndex(sortedScheduleTournaments, activeScheduleStartIndex),
        [sortedScheduleTournaments, activeScheduleStartIndex]
    );
    const lobbyLiveRowsRequest = useMemo(
        () => buildTournamentLobbyLiveRowsRequest(schedulePage.items),
        [schedulePage.items]
    );
    const lobbyLiveRowsQuery = useTournamentLobbyLiveRows(lobbyLiveRowsRequest);
    const lobbyLiveTournamentRows = useMemo(
        () => getTournamentLobbyLiveRowsForRequest(lobbyLiveRowsRequest, lobbyLiveRowsQuery.data),
        [lobbyLiveRowsRequest, lobbyLiveRowsQuery.data]
    );
    const visibleTournaments = useMemo(
        () => sortTournamentLobbyRows(
            mergeScheduleTournamentsWithLobbyLiveRows(schedulePage.items, lobbyLiveTournamentRows),
            tournamentSortModel,
            currentTime,
            userState.id
        ),
        [schedulePage.items, lobbyLiveTournamentRows, tournamentSortModel, currentTime, userState.id]
    );

    const modalBaseTournament = useMemo(() => (
        modalTournamentId
            ? visibleTournaments.find(t => t.gameId === modalTournamentId || t.tournamentId === modalTournamentId) ||
                sortedScheduleTournaments.find(t => t.gameId === modalTournamentId || t.tournamentId === modalTournamentId) ||
                null
            : null
    ), [modalTournamentId, visibleTournaments, sortedScheduleTournaments]);
    const modalDetailsRequest = useMemo(
        () => buildTournamentLobbyDetailsRequest(modalTournamentId),
        [modalTournamentId]
    );
    const modalDetailsQuery = useTournamentLobbyDetails(modalDetailsRequest);
    const modalDetailsTournament = useMemo(
        () => getTournamentLobbyDetailsTournament(modalDetailsQuery.data),
        [modalDetailsQuery.data]
    );
    const modalTournament = useMemo(() => {
        if (!modalBaseTournament) return modalDetailsTournament;
        return mergeScheduleTournamentsWithRealtime(
            [modalBaseTournament],
            modalDetailsTournament ? [modalDetailsTournament] : []
        )[0] || modalBaseTournament;
    }, [modalBaseTournament, modalDetailsTournament]);

    useEffect(() => {
        logTournamentLobbyTournamentDebug('desktop-modal-tournament', modalTournament, {
            modalTournamentId,
        });
    }, [modalTournament, modalTournamentId]);

    useEffect(() => {
        if (selectedTournament) {
            const updatedTournament = visibleTournaments.find(
                tournament => tournament.gameId === selectedTournament.gameId
            );

            if (updatedTournament) {
                if (updatedTournament !== selectedTournament) setSelectedTournament(updatedTournament);
            } else {
                setSelectedTournament(null);
            }
        }
    }, [visibleTournaments, selectedTournament]);

    const scheduleDateLabel = isScheduleGlobalPagination
        ? formatTournamentScheduleGlobalSegmentLabel(schedulePage)
        : formatTournamentScheduleSegmentLabel(scheduleFilters.date, schedulePage);
    const tournamentCountLabel = scheduleQuery.isFetching
        ? 'Refreshing...'
        : `${schedulePage.startItemNumber}-${schedulePage.endItemNumber} of ${schedulePage.totalItems} tournaments`;
    const previousSchedulePageDisabled = scheduleQuery.isFetching || (
        isScheduleGlobalPagination && schedulePage.startIndex <= 0
    );
    const nextSchedulePageDisabled = scheduleQuery.isFetching || (
        isScheduleGlobalPagination && schedulePage.endItemNumber >= schedulePage.totalItems
    );

    return (
        <ThemeProvider theme={darkTheme}>
            <div style={{ display: 'flex', width: '100%', height: 'calc(100dvh - 140px)', gap: '1rem', padding: '1rem 1rem 0 1rem', boxSizing: 'border-box' }}>
                <div style={{ width: 'calc(100% - 1rem - 360px)', height: '100%', flexGrow: 0, flexShrink: 0, boxSizing: 'border-box', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ minHeight: 40, flexWrap: 'nowrap', color: 'rgba(255,255,255,0.72)' }}>
                        <TournamentScheduleFilterBar
                            value={scheduleFilters}
                            onChange={handleScheduleFilterChange}
                            showPresets
                            showReset={false}
                        />
                        <Box sx={{ flexGrow: 1 }} />
                        <Typography
                            variant="body2"
                            sx={{
                                fontSize: '12px',
                                color: 'rgba(255,255,255,0.55)',
                                whiteSpace: 'nowrap',
                                fontVariantNumeric: 'tabular-nums',
                            }}
                        >
                            {tournamentCountLabel}
                        </Typography>
                        {/* Segmented day pager: Prev / date / Next read as a single control. */}
                        <Stack
                            direction="row"
                            alignItems="center"
                            sx={{
                                height: 36,
                                flexShrink: 0,
                                backgroundColor: '#1B1C22',
                                border: '1px solid rgba(255,255,255,0.10)',
                                borderRadius: '9px',
                                overflow: 'hidden',
                            }}
                        >
                            <Button
                                disableRipple
                                aria-label="Previous"
                                disabled={previousSchedulePageDisabled}
                                onClick={handlePreviousSchedulePage}
                                sx={{
                                    height: '100%',
                                    minWidth: 36,
                                    px: 0.5,
                                    color: '#C7CEDA',
                                    borderRadius: 0,
                                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.07)' },
                                    '&.Mui-disabled': { color: 'rgba(255,255,255,0.25)' },
                                }}
                            >
                                <KeyboardArrowLeft fontSize="small" />
                            </Button>
                            <Typography
                                variant="body2"
                                sx={{
                                    minWidth: 96,
                                    px: 1,
                                    textAlign: 'center',
                                    fontSize: '12px',
                                    fontWeight: 600,
                                    color: '#FFFFFF',
                                    whiteSpace: 'nowrap',
                                    fontVariantNumeric: 'tabular-nums',
                                    borderLeft: '1px solid rgba(255,255,255,0.10)',
                                    borderRight: '1px solid rgba(255,255,255,0.10)',
                                }}
                            >
                                {scheduleDateLabel}
                            </Typography>
                            <Button
                                disableRipple
                                aria-label="Next"
                                disabled={nextSchedulePageDisabled}
                                onClick={handleNextSchedulePage}
                                sx={{
                                    height: '100%',
                                    minWidth: 36,
                                    px: 0.5,
                                    color: '#C7CEDA',
                                    borderRadius: 0,
                                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.07)' },
                                    '&.Mui-disabled': { color: 'rgba(255,255,255,0.25)' },
                                }}
                            >
                                <KeyboardArrowRight fontSize="small" />
                            </Button>
                        </Stack>
                        <Button
                            variant="outlined"
                            onClick={resetScheduleFilters}
                            sx={{
                                height: 36,
                                flexShrink: 0,
                                color: '#C7CEDA',
                                borderColor: 'rgba(255,255,255,0.16)',
                                borderRadius: '8px',
                                textTransform: 'none',
                                fontWeight: 600,
                                fontSize: '13px',
                                '&:hover': {
                                    borderColor: 'rgba(255,255,255,0.32)',
                                    backgroundColor: 'rgba(255,255,255,0.05)',
                                },
                            }}
                        >
                            Reset
                        </Button>
                    </Stack>
                    <Box sx={{ flex: 1, minHeight: 0 }}>
                        {isLoading ? (
                            <div style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                height: '100%',
                                paddingBottom: '1rem',
                                color: 'white'
                            }}>
                                <img
                                    src={`${import.meta.env.BASE_URL}images/loading-trophy.gif`}
                                    alt="Loading tournaments..."
                                    style={{ width: '80px', height: '80px', marginBottom: '16px' }}
                                />
                                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                                    {t('Tournament:info.loading')}
                                </Typography>
                            </div>
                        ) : (
                            <DataGrid
                            disableColumnMenu
                            disableColumnFilter
                            getCellClassName={() => styles.tableCell}
                            rows={visibleTournaments}
                            columns={columns}
                            classes={customDataGridClasses}
                            sortingMode="server"
                            sortModel={tournamentSortModel}
                            onSortModelChange={handleTournamentSortModelChange}
                            sortingOrder={['asc', 'desc']}
                            pageSizeOptions={[500]}
                            getRowId={(row) => row.gameId}
                            hideFooter
                            onRowClick={handleRowClick}
                            onRowDoubleClick={handleRowDoubleClick}
                            autoHeight={false}
                            disableVirtualization={false}
                            getRowClassName={(params) => {
                                // Check if user is registered for this tournament
                                const isRegisteredForThis = isTournamentRegisteredByUser(params.row, userState.id);
                                return isRegisteredForThis ? styles.active : styles.tableRow;
                            }}
                            slots={{
                                columnSortedDescendingIcon: ArrowDropUp,
                                columnSortedAscendingIcon: ArrowDropDown,
                            }}
                            sx={{
                                border: 'none',
                                color: 'white',
                                height: '100%',
                                '& .MuiDataGrid-virtualScroller': {
                                    '&::-webkit-scrollbar': {
                                        width: '0.4em',
                                    },
                                    '&::-webkit-scrollbar-track': {
                                        background: 'transparent',
                                    },
                                    '&::-webkit-scrollbar-thumb': {
                                        backgroundColor: 'rgba(255,255,255,.3)',
                                    },
                                    '&::-webkit-scrollbar-thumb:hover': {
                                        background: 'rgba(255,255,255,.5)',
                                    },
                                },
                                '& .MuiDataGrid-main': {
                                    gap: '0.25rem'
                                },
                                '& .MuiDataGrid-virtualScrollerRenderZone': {
                                    gap: '0.25rem',
                                    width: '100%',
                                    boxSizing: 'border-box',
                                    paddingBottom: '1rem'
                                },
                                '& .MuiDataGrid-root': {
                                    fontFamily: 'Inter, serif'
                                },
                                '& .MuiDataGrid-columnHeader': {
                                    fontFamily: 'Inter, serif'
                                },
                                '& .MuiDataGrid-cell': {
                                    fontFamily: 'Inter, serif'
                                },
                                '& .MuiDataGrid-cell:first-of-type': {
                                    borderLeft: '2px solid #F52740 !important',
                                },
                                '& .MuiDataGrid-row': {
                                    cursor: 'pointer',
                                    background: 'linear-gradient(90deg, rgba(245, 39, 64, 0.10) 0%, rgba(28, 28, 37, 0.10) 22.39%), #2A2A32',
                                    transition: 'background 300ms ease',
                                    width: '100%'
                                },
                                '& .MuiDataGrid-row:hover': {
                                    cursor: 'pointer',
                                    background: '#40414C !important',
                                },
                                '& .MuiDataGrid-row:focus': {
                                    backgroundColor: '#40414C',
                                },
                                '& .MuiDataGrid-row:active': {
                                    backgroundColor: '#40414C',
                                },
                                '& .MuiDataGrid-sortIcon': {
                                    color: '#66666b',
                                    fontSize: '2rem'
                                },
                                '& .MuiDataGrid-row.Mui-selected': {
                                    backgroundColor: '#40414C',
                                },
                                '& .MuiDataGrid-columnSeparator': {
                                    display: 'none !important',
                                },
                            }}
                            />
                        )}
                    </Box>
                </div>
                <Box style={{ display: 'flex', flexDirection: 'column', width: '360px', height: '100%', flexGrow: 0, flexShrink: 0, paddingBottom: '1rem', boxSizing: 'border-box' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', color: 'white', height: 'calc(100% - 115px)', backgroundColor: '#2a2b32', mb: '15px' }}>
                        <Stack sx={{ height: '100%' }}>
                            <Typography variant="h6" align="center" className={styles.boxTitle}>{t('Tournament:title')}</Typography>
                            <Box sx={{ 
                                width: '100%',
                                maxHeight: '100%',
                                boxSizing: 'border-box',
                                bgcolor: 'transparent',
                                overflow: 'auto',
                                marginBottom: '20px',
                                p: 2,
                                pb: 0,
                                '& .MuiTypography-root': {
                                    fontFamily: '"Inter", serif'
                                },
                                '&::-webkit-scrollbar': {
                                    width: '0.4em'
                                },
                                '&::-webkit-scrollbar-track': {
                                },
                                '&::-webkit-scrollbar-thumb': {
                                    backgroundColor: 'white',
                                    borderRadius: '4px'
                                }
                            }}>
                                {selectedTournament ? (
                                    <>
                                        <Typography variant="h6" gutterBottom sx={{ fontWeight: '600', color: '#ffffff', fontSize: '20px', lineHeight: '28px', mb: 3 }}>{selectedTournament.templateName || selectedTournament.name}</Typography>
                                        {/* {selectedTournament.description && <Typography variant="body2" gutterBottom sx={{ color: '#ccc' }}>{selectedTournament.description}</Typography>} */}

                                        {/* Tournament Details */}
                                        <Box sx={{ m: 0 }}>
                                            <Typography variant="body1" sx={{ display: 'flex', mb: 1.5 }}>
                                                <span style={{ color: 'rgba(255,255,255,0.5)'}}>{t('Tournament:info.registration')}</span>
                                                <span style={{ marginLeft: 'auto' }}>
                                                    {(() => {
                                                        const { registrationStart, registrationEnd, lateRegistrationEnd } = getRegistrationTimesLocal(selectedTournament);
                                                        const now = currentTime;

                                                        if (!registrationStart) return t('Tournament:labels.na');

                                                        // Not yet open
                                                        if (now < registrationStart) {
                                                            return formatTimeDifferenceLocal(registrationStart, now);
                                                        }

                                                        // Open and in normal registration window
                                                        if (registrationEnd && now < registrationEnd) {
                                                            return (
                                                                <>
                                                                    {t('Tournament:registration.opens')}{' '}
                                                                    <Typography component="span" variant="body1" sx={{ fontSize: '0.8em', color: 'rgba(255,255,255,0.7)' }}>
                                                                        ({formatTimeDifferenceLocal(registrationEnd, now)})
                                                                    </Typography>
                                                                </>
                                                            );
                                                        }

                                                        // Late registration window
                                                        if (lateRegistrationEnd && selectedTournament.lateRegistrationMinutes) {
                                                            if (isLateRegistrationOpenLocal(selectedTournament, now) && selectedTournament.status !== 'completed' && selectedTournament.status !== 'cancelled') {
                                                                return (
                                                                    <>
                                                                        {t('Tournament:status.lateRegistration')}{' '}
                                                                        <Typography component="span" variant="body1" sx={{ fontSize: '0.8em', color: 'rgba(255,255,255,0.7)' }}>
                                                                            ({formatTimeDifferenceLocal(lateRegistrationEnd, now)})
                                                                        </Typography>
                                                                    </>
                                                                );
                                                            }
                                                        }

                                                        // After all registration windows
                                                        return t('Tournament:registration.closes');
                                                    })()}
                                                </span>
                                            </Typography>
                                            <Typography variant="body1" sx={{ display: 'flex', mb: 1.5 }}>
                                                <span style={{ color: 'rgba(255,255,255,0.5)'}}>{t('Tournament:info.startTime')}</span>
                                                <span style={{ marginLeft: 'auto' }}>
                                                    {(() => {
                                                        const startTime = new Date(selectedTournament.startTime);

                                                        const options: Intl.DateTimeFormatOptions = {
                                                            hour: '2-digit',
                                                            minute: '2-digit',
                                                            day: '2-digit',
                                                            month: 'short',
                                                            hour12: false,
                                                        };
                                                        
                                                        const formattedStartTime = startTime.toLocaleString('en-GB', options);

                                                        return <span>
                                                            {formattedStartTime}
                                                            <Typography component="span" variant="body1" sx={{ fontSize: '0.8em', color: 'rgba(255,255,255,0.7)', ml: 0.5 }}>
                                                                {(() => {
                                                                    const startTime = new Date(selectedTournament.startTime);
                                                                    if (currentTime < startTime) {
                                                                        return `(${formatTimeDifferenceLocal(startTime, currentTime)})`;
                                                                    }
                                                                    if (selectedTournament.status === 'completed') {
                                                                        return `(${t('Tournament:status.ended')})`;
                                                                    } else if (selectedTournament.status === 'cancelled') {
                                                                        return `(${t('Tournament:status.cancelled')})`;
                                                                    } else {
                                                                        return `(${t('Tournament:info.started')})`;
                                                                    }
                                                                })()}
                                                            </Typography>
                                                            </span>;
                                                    })()}
                                                </span>
                                            </Typography>
                                            <Typography variant="body1" sx={{ display: 'flex', mb: 1.5 }}>
                                                <span style={{ color: 'rgba(255,255,255,0.5)'}}>{t('Tournament:info.buyIn')}</span>
                                                <span style={{ marginLeft: 'auto' }}>
                                                    {selectedTournament.buyIn === 0 ? t('Tournament:gameTypes.freeroll') : `${formatCurrencyLocal(selectedTournament.buyIn)} + ${formatCurrencyLocal(selectedTournament.entryFee)}`}
                                                </span>
                                            </Typography>
                                            <Typography variant="body1" sx={{ display: 'flex', mb: 1.5 }}>
                                                <span style={{ color: 'rgba(255,255,255,0.5)'}}>{t('Tournament:info.prize')}</span>
                                                <span style={{ marginLeft: 'auto' }}>
                                                    {formatCurrencyLocal(getEffectivePrizeLocal(selectedTournament))}
                                                    {selectedTournament.ticketPrizes && selectedTournament.ticketPrizes.length > 0 && (
                                                        <TicketPrizesTooltip ticketPrizes={selectedTournament.ticketPrizes}>
                                                            <span style={{ cursor: 'help' }}>
                                                                {selectedTournament.ticketPrizes!.length === 1 ? ` + ${t('Tournament:labels.ticket')}` : ` + ${t('Tournament:labels.tickets')}`}
                                                            </span>
                                                        </TicketPrizesTooltip>
                                                    )}
                                                </span>
                                            </Typography>
                                            <Typography variant="body1" sx={{ display: 'flex', mb: 1.5 }}>
                                                <span style={{ color: 'rgba(255,255,255,0.5)'}}>{t('Tournament:info.playersRegistered')}</span>
                                                <span style={{ marginLeft: 'auto' }}>{selectedTournament.registeredPlayerCount}</span>
                                            </Typography>
                                            {selectedTournament.minPlayers > 0 && (
                                                <Typography variant="body1" sx={{ display: 'flex', mb: 1.5 }}>
                                                    <span style={{ color: 'rgba(255,255,255,0.5)'}}>{t('Tournament:info.minPlayers')}</span>
                                                    <span style={{ marginLeft: 'auto' }}>{selectedTournament.minPlayers}</span>
                                                </Typography>
                                            )}
                                            <Typography variant="body1" sx={{ display: 'flex', mb: 1.5 }}>
                                                <span style={{ color: 'rgba(255,255,255,0.5)'}}>{t('Tournament:info.rebuysAddons')}</span>
                                                <span style={{ marginLeft: 'auto' }}>{t('Tournament:labels.na')}</span>
                                            </Typography>
                                            <Typography variant="body1" sx={{ display: 'flex', mb: 1.5 }}>
                                                <span style={{ color: 'rgba(255,255,255,0.5)'}}>{t('Tournament:info.startingStack')}</span>
                                                <span style={{ marginLeft: 'auto' }}>
                                                    {selectedTournament.startingChips?.toLocaleString(undefined, { maximumFractionDigits: 0 })}{selectedTournament.currency ? ` ${selectedTournament.currency}` : ''}
                                                </span>
                                            </Typography>
                                            <Typography variant="body1" sx={{ display: 'flex', mb: 0 }}>
                                                <span style={{ color: 'rgba(255,255,255,0.5)'}}>{t('Tournament:info.gameType')}</span>
                                                <span style={{ marginLeft: 'auto' }}>
                                                    {selectedTournament.type === 'aof' ? t('Tournament:gameTypes.aof') : (selectedTournament.type === 'texas' ? t('Tournament:gameTypes.holdem') : selectedTournament.type.toUpperCase())}
                                                </span>
                                            </Typography>
                                            {/* <Typography variant="body2" sx={{ mb: 0.5 }}>
                                                <span>Start Time</span> {formatDateTime(selectedTournament.startTime)}{' '}
                                                {(() => {
                                                    const startTime = new Date(selectedTournament.startTime);
                                                    if (currentTime < startTime) {
                                                        return <span style={{ color: '#64dfdf' }}>(time left: {formatTimeDifference(startTime, currentTime)})</span>;
                                                    }
                                                    if (selectedTournament.status === 'completed') {
                                                        return <span style={{ color: '#16EFC1' }}>(ended)</span>;
                                                    } else if (selectedTournament.status === 'cancelled') {
                                                        return <span style={{ color: '#B3B3B3' }}>(cancelled)</span>;
                                                    } else {
                                                        return <span style={{ color: '#ff9e80' }}>(started)</span>;
                                                    }
                                                })()}
                                            </Typography> */}
                                            
                                            {/* Registration Timing Information */}
                                            {/* {selectedTournament.registrationDurationMinutes !== undefined && (
                                                <Box sx={{ mt: 1, mb: 0.5 }}>
                                                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                                                        <strong>Registration Opens:</strong>{' '}
                                                        {(() => {
                                                            const { registrationStart } = getRegistrationTimes(selectedTournament);
                                                            if (!registrationStart) return t('Tournament:labels.na');
                                                            
                                                            const statusText = currentTime < registrationStart 
                                                                ? ` (${formatTimeDifference(registrationStart, currentTime)})`
                                                                : ' (Open now)';
                                                            
                                                            return `${formatDateTime(registrationStart.toISOString())}${statusText}`;
                                                        })()}
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                                                        <strong>Registration Closes:</strong>{' '}
                                                        {(() => {
                                                            const { registrationEnd, lateRegistrationEnd } = getRegistrationTimes(selectedTournament);
                                                            
                                                            // If tournament has late registration
                                                            if (lateRegistrationEnd && selectedTournament.lateRegistrationMinutes) {
                                                                const now = currentTime;
                                                                if (now > lateRegistrationEnd) {
                                                                    return 'Closed';
                                                                }
                                                                
                                                                const timeLeft = now < lateRegistrationEnd ? 
                                                                    ` (${formatTimeDifference(lateRegistrationEnd, now)})` : '';
                                                                
                                                                return `${formatDateTime(lateRegistrationEnd.toISOString())}${timeLeft}`;
                                                            }
                                                            
                                                            // Regular registration (no late registration)
                                                            if (registrationEnd) {
                                                                const now = currentTime;
                                                                if (now > registrationEnd) {
                                                                    return 'Closed';
                                                                }
                                                                
                                                                const timeLeft = now < registrationEnd ? 
                                                                    ` (${formatTimeDifference(registrationEnd, now)})` : '';
                                                                
                                                                return `At tournament start${timeLeft}`;
                                                            }
                                                            
                                                            return 'N/A';
                                                        })()}
                                                    </Typography>
                                                    {selectedTournament.registrationDurationMinutes ? (
                                                        <Typography variant="body2" sx={{ mb: 0.5, color: '#64dfdf' }}>
                                                            <strong>Registration Period:</strong> {selectedTournament.registrationDurationMinutes} minutes before start
                                                        </Typography>
                                                    ) : null}
                                                    {selectedTournament.lateRegistrationMinutes ? (
                                                        <Typography variant="body2" sx={{ color: '#e9c46a' }}>
                                                            <strong>Late Registration:</strong> {selectedTournament.lateRegistrationMinutes} minutes after start
                                                        </Typography>
                                                    ) : null}
                                                </Box>
                                            )} */}
                                        </Box>

                                        {/* Registered Players Section */}
                                        {/* <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>Registered Players ({selectedTournament.registeredPlayerCount})</Typography>
                                        {(selectedTournament.playerList && selectedTournament.playerList.length > 0) ? (
                                            <List dense sx={{
                                                maxHeight: isSmallScreen ? '150px' : '200px',
                                                overflowY: 'auto',
                                                '&::-webkit-scrollbar': { width: '0.4em' },
                                                '&::-webkit-scrollbar-track': { background: 'transparent' },
                                                '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(255,255,255,.3)' },
                                                '&::-webkit-scrollbar-thumb:hover': { background: 'rgba(255,255,255,.5)' },
                                                mb: 3,
                                                display: 'grid',
                                                gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
                                                gap: '4px'
                                            }}>
                                                {selectedTournament.playerList.map((player) => (
                                                    <ListItem key={player.userId} sx={{ 
                                                        padding: '4px 8px',
                                                        borderRadius: '4px',
                                                        backgroundColor: 'rgba(30, 30, 40, 0.3)',
                                                        minWidth: 0
                                                    }}>
                                                        {player.countryCode ? (
                                                            <ReactCountryFlag
                                                                countryCode={player.countryCode}
                                                                svg
                                                                style={{ width: '16px', height: '16px', marginRight: '6px' }}
                                                                title={player.countryCode}
                                                            />
                                                        ) : (
                                                            <span style={{ width: '16px', height: '16px', marginRight: '6px' }}>?</span>
                                                        )}
                                                        <ListItemText
                                                            primary={player.username}
                                                            primaryTypographyProps={{ 
                                                                fontSize: '0.85rem',
                                                                whiteSpace: 'nowrap',
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis'
                                                            }}
                                                            sx={{ margin: 0 }}
                                                        />
                                                    </ListItem>
                                                ))}
                                            </List>
                                        ) : (
                                            <Typography variant="body2" sx={{ color: '#888', mb: 3 }}>No players registered yet.</Typography>
                                        )} */}

                                        {/* Rankings Section */}
                                        {/* {selectedTournament.rankings && selectedTournament.rankings.length > 0 && (
                                            <Box sx={{ mt: 2, mb: 3 }}>
                                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>Tournament Rankings</Typography>
                                                <TableContainer 
                                                    component={Paper} 
                                                    sx={{ 
                                                        maxHeight: isSmallScreen ? '150px' : '250px',
                                                        backgroundColor: 'rgba(30, 30, 40, 0.5)',
                                                        '&::-webkit-scrollbar': { width: '0.4em' },
                                                        '&::-webkit-scrollbar-track': { background: 'transparent' },
                                                        '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(255,255,255,.3)' },
                                                        '&::-webkit-scrollbar-thumb:hover': { background: 'rgba(255,255,255,.5)' },
                                                    }}
                                                >
                                                    <Table size="small" stickyHeader>
                                                        <TableHead>
                                                            <TableRow>
                                                                <TableCell 
                                                                    sx={{ 
                                                                        color: '#fff', 
                                                                        fontWeight: 'bold', 
                                                                        backgroundColor: '#1e1e28',
                                                                        borderBottom: '1px solid #333',
                                                                        padding: '6px 10px'
                                                                    }}
                                                                >
                                                                    Pos
                                                                </TableCell>
                                                                <TableCell 
                                                                    sx={{ 
                                                                        color: '#fff', 
                                                                        fontWeight: 'bold', 
                                                                        backgroundColor: '#1e1e28',
                                                                        borderBottom: '1px solid #333',
                                                                        padding: '6px 10px'
                                                                    }}
                                                                >
                                                                    Player
                                                                </TableCell>
                                                                <TableCell 
                                                                    align="right" 
                                                                    sx={{ 
                                                                        color: '#fff', 
                                                                        fontWeight: 'bold', 
                                                                        backgroundColor: '#1e1e28',
                                                                        borderBottom: '1px solid #333',
                                                                        padding: '6px 10px'
                                                                    }}
                                                                >
                                                                    Chips
                                                                </TableCell>
                                                                <TableCell 
                                                                    align="right" 
                                                                    sx={{ 
                                                                        color: '#fff', 
                                                                        fontWeight: 'bold', 
                                                                        backgroundColor: '#1e1e28',
                                                                        borderBottom: '1px solid #333',
                                                                        padding: '6px 10px'
                                                                    }}
                                                                >
                                                                    Registration
                                                                </TableCell>
                                                                <TableCell 
                                                                    align="right" 
                                                                    sx={{ 
                                                                        color: '#fff', 
                                                                        fontWeight: 'bold', 
                                                                        backgroundColor: '#1e1e28',
                                                                        borderBottom: '1px solid #333',
                                                                        padding: '6px 10px'
                                                                    }}
                                                                >
                                                                    Eliminated
                                                                </TableCell>
                                                            </TableRow>
                                                        </TableHead>
                                                        <TableBody>
                                                            {selectedTournament.rankings
                                                                .sort((a, b) => b.balance - a.balance)
                                                                .map((player, index) => {
                                                                    const isEliminated = Boolean(player.eliminationTime);
                                                                    const isHighlighted = player.userId === userState.id;

                                                                    return (
                                                                        <TableRow 
                                                                            key={player.userId}
                                                                            sx={{ 
                                                                                backgroundColor: isHighlighted ? 'rgba(249, 55, 81, 0.1)' : 'transparent',
                                                                                '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.05)' },
                                                                            }}
                                                                        >
                                                                            <TableCell 
                                                                                sx={{ 
                                                                                    color: '#fff', 
                                                                                    borderBottom: '1px solid #333',
                                                                                    fontWeight: isHighlighted ? 'bold' : 'normal',
                                                                                    padding: '4px 10px'
                                                                                }}
                                                                            >
                                                                                {index + 1}
                                                                            </TableCell>
                                                                            <TableCell 
                                                                                sx={{ 
                                                                                    color: isHighlighted ? '#F93751' : '#fff', 
                                                                                    borderBottom: '1px solid #333',
                                                                                    fontWeight: isHighlighted ? 'bold' : 'normal',
                                                                                    padding: '4px 10px'
                                                                                }}
                                                                            >
                                                                                {player.username}
                                                                            </TableCell>
                                                                            <TableCell 
                                                                                align="right"
                                                                                sx={{ 
                                                                                    color: isEliminated ? '#888' : '#64dfdf', 
                                                                                    borderBottom: '1px solid #333',
                                                                                    fontWeight: isHighlighted ? 'bold' : 'normal',
                                                                                    padding: '4px 10px'
                                                                                }}
                                                                            >
                                                                                {isEliminated ? 
                                                                                    <Tooltip title={`Eliminated with ${player.chipsBeforeElimination || 0} chips`} placement="top">
                                                                                        <span>0</span>
                                                                                    </Tooltip> 
                                                                                    : player.balance.toLocaleString()
                                                                                }
                                                                            </TableCell>
                                                                            <TableCell 
                                                                                align="right"
                                                                                sx={{ 
                                                                                    color: '#64dfdf', 
                                                                                    borderBottom: '1px solid #333',
                                                                                    fontSize: '0.8rem',
                                                                                    fontWeight: isHighlighted ? 'bold' : 'normal',
                                                                                    padding: '4px 10px'
                                                                                }}
                                                                            >
                                                                                {player.registrationTime ? formatDate(player.registrationTime) : '-'}
                                                                            </TableCell>
                                                                            <TableCell 
                                                                                align="right"
                                                                                sx={{ 
                                                                                    color: isEliminated ? '#ff9e80' : '#1BE958', 
                                                                                    borderBottom: '1px solid #333',
                                                                                    fontSize: '0.8rem',
                                                                                    padding: '4px 10px'
                                                                                }}
                                                                            >
                                                                                {isEliminated ? 
                                                                                    formatDate(player.eliminationTime || null)
                                                                                    : (index === 0 && selectedTournament.status === 'completed' ? 
                                                                                        <span style={{ color: '#FFC100', fontWeight: 'bold' }}>Winner</span> 
                                                                                        : 'Active')
                                                                                }
                                                                            </TableCell>
                                                                        </TableRow>
                                                                    );
                                                                })}
                                                        </TableBody>
                                                    </Table>
                                                </TableContainer>
                                            </Box>
                                        )} */}

                                        {/* Active Rooms Section */}
                                        {/* {selectedTournament.activeRooms && selectedTournament.activeRooms.length > 0 && (
                                            <Box sx={{ mt: 2, mb: 3 }}>
                                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>Active Tables</Typography>
                                                <List dense sx={{ maxHeight: '200px', overflowY: 'auto', '&::-webkit-scrollbar': { width: '0.4em' }, '&::-webkit-scrollbar-track': { background: 'transparent' }, '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(255,255,255,.3)' } }}>
                                                    {selectedTournament.activeRooms.map((room) => (
                                                        <ListItem 
                                                            key={room.roomId} 
                                                            sx={{ 
                                                                padding: '8px', 
                                                                backgroundColor: 'rgba(30, 30, 40, 0.5)', 
                                                                borderRadius: '4px',
                                                                mb: 1,
                                                                display: 'flex',
                                                                justifyContent: 'space-between',
                                                                alignItems: 'center'
                                                            }}
                                                        >
                                                            <ListItemText 
                                                                primary={`${room.tableName}${room.isFinalTournamentTable ? ' (FINAL)' : ''}`} 
                                                                secondary={`Players: ${getTournamentRoomActivePlayerCount(room)}${room.maximumPlayers ? ' / ' + room.maximumPlayers : ''}`}
                                                                primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: 'medium' }}
                                                                secondaryTypographyProps={{ color: '#aaa' }}
                                                                sx={{ flex: '1 1 auto' }}
                                                            />
                                                            <Button
                                                                variant="contained"
                                                                size="small"
                                                                sx={{ 
                                                                    ml: 2, 
                                                                    textTransform: 'capitalize',
                                                                    minWidth: '100px'
                                                                }}
                                                                onClick={() => handleJoinTournamentRoom(room.roomId)}
                                                            >
                                                                Open Table
                                                            </Button>
                                                        </ListItem>
                                                    ))}
                                                </List>
                                            </Box>
                                        )} */}

                                        {/* Sign Up / Unsign Button */}
                                        {/* {selectedTournament && selectedTournament.status === 'registration' && (
                                            <Box sx={{ mt: 0, textAlign: 'center' }}>
                                                {isUserRegistered ? (
                                                    <Button
                                                        variant="contained"
                                                        color="error"
                                                        onClick={handleUnregisterClick}
                                                        fullWidth
                                                    >
                                                        Unsign
                                                    </Button>
                                                ) : selectedTournament.registeredPlayerCount < selectedTournament.maxPlayers ? (
                                                    <Button
                                                        variant="contained"
                                                        color="success"
                                                        onClick={handleRegisterClick}
                                                        fullWidth
                                                    >
                                                        Sign Up
                                                    </Button>
                                                ) : (
                                                    <Typography variant="body2" sx={{ color: '#ff9e80', fontWeight: 'bold', mb: 1 }}>
                                                        Tournament is full
                                                    </Typography>
                                                )}
                                            </Box>
                                        )} */}
                                    </>
                                ) : (
                                    <Typography align="center" className={styles.placeholder}>{t('Tournament:lobby.noTournamentSelected')}</Typography>
                                )}
                            </Box>
                            {selectedTournament && (
                                <Stack>
                                    <Button
                                        variant="contained"
                                        color="secondary"
                                        style={{ color: 'white', textTransform: 'capitalize', fontWeight: 700, fontSize: '16px' }}
                                        sx={{ mx: '20px', mt: 'auto', mb: '10px' }}
                                        onClick={() => handleTournamentLobbyButton(selectedTournament.gameId)}
                                    >
                                        {t('Tournament:actions.openLobby')}
                                    </Button>
                                    {isUserRegistered ? (() => {
                                        const isStarted = currentTime >= new Date(selectedTournament.startTime);
                                        return (
                                            <Button
                                                variant="contained"
                                                color={isStarted ? "secondary" : "primary"}
                                                style={{ 
                                                    color: isStarted ? 'rgba(255,255,255,0.4)' : 'white', 
                                                    textTransform: 'capitalize', 
                                                    fontWeight: 700, 
                                                    fontSize: '16px' 
                                                }}
                                                sx={{ mx: '20px', mt: 'auto', mb: '20px' }}
                                                onClick={handleUnregisterClick}
                                                disabled={isStarted || actionCooldown}
                                            >
                                                {t('Tournament:actions.unregister')}
                                            </Button>
                                        );
                                    })() : (() => {
                                        const canRegisterNow = canRegisterForTournament(selectedTournament, currentTime);
                                        const eligibleTickets = getEligibleTickets(userState.tickets || [], selectedTournament);
                                        const entryMode = selectedTournament.registrationEntryMode || 'cash_only';
                                        const cashAllowed = entryMode === 'cash_only' || entryMode === 'cash_or_ticket';
                                        const ticketAllowed = entryMode === 'ticket_only' || entryMode === 'cash_or_ticket';
                                        const hasEligibleTicket = eligibleTickets.length > 0;

                                        return canRegisterNow ? (
                                            selectedTournament.registeredPlayerCount < selectedTournament.maxPlayers ? (
                                                <>
                                                    {cashAllowed && (
                                                        <Button
                                                            variant="contained"
                                                            style={{ color: 'white', textTransform: 'capitalize', fontWeight: 700, fontSize: '16px' }}
                                                            sx={{ mx: '20px', mt: 'auto', mb: ticketAllowed ? '10px' : '20px' }}
                                                            onClick={handleRegisterClick}
                                                            disabled={actionCooldown}
                                                        >
                                                            {t('Tournament:actions.register')}
                                                        </Button>
                                                    )}
                                                    {ticketAllowed && hasEligibleTicket && (
                                                        <Button
                                                            variant="contained"
                                                            color="primary"
                                                            style={{ color: 'white', textTransform: 'capitalize', fontWeight: 700, fontSize: '16px' }}
                                                            sx={{ mx: '20px', mb: '20px', display: 'flex', alignItems: 'center', gap: 1 }}
                                                            onClick={() => handleRegisterWithTicket(eligibleTickets[0]._id, selectedTournament.gameId)}
                                                            disabled={actionCooldown}
                                                        >
                                                            <ConfirmationNumberIcon fontSize="small" />
                                                            {t('Tournament:labels.registerWithTicket')}
                                                        </Button>
                                                    )}
                                                    {ticketAllowed && !hasEligibleTicket && entryMode === 'ticket_only' && (
                                                        <Button
                                                            variant="contained"
                                                            color="secondary"
                                                            disabled
                                                            style={{ textTransform: 'capitalize', fontWeight: 700, fontSize: '16px' }}
                                                            sx={{ mx: '20px', mb: '20px' }}
                                                        >
                                                            {t('Tournament:labels.ticketRequired')}
                                                        </Button>
                                                    )}
                                                </>
                                            ) : (
                                                <Button
                                                    variant="contained"
                                                    color="secondary"
                                                    style={{ color: 'rgba(255,255,255,0.4)', textTransform: 'capitalize', fontWeight: 700, fontSize: '16px' }}
                                                    sx={{ mx: '20px', mt: 'auto', mb: '20px' }}
                                                    disabled
                                                >
                                                    {t('Tournament:labels.tournamentFull')}
                                                </Button>
                                            )
                                        ) : (
                                            <Button
                                                variant="contained"
                                                color="secondary"
                                                style={{ color: 'rgba(255,255,255,0.4)', textTransform: 'capitalize', fontWeight: 700, fontSize: '16px' }}
                                                sx={{ mx: '20px', mt: 'auto', mb: '20px' }}
                                                disabled
                                            >
                                                {t('Tournament:labels.registrationClosed')}
                                            </Button>
                                        );
                                    })()}
                                </Stack>
                            )}
                        </Stack>
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', color: 'white', height: '100px', backgroundColor: '#2a2b32', p: '20px', boxSizing: 'border-box' }}>
                        <Stack sx={{ height: '100%' }}>
                            <Typography style={{ display: 'flex', alignItems: 'center', marginBottom: '15px' }}><CircleIcon sx={{ width: '10px', height: '10px', color: '#3A3D50', mr: '10px' }}/>{t('Tournament:lobby.clickedTournament')}</Typography>
                            <Typography style={{ display: 'flex', alignItems: 'center' }}><CircleIcon sx={{ width: '10px', height: '10px', color: '#2E6F6B', mr: '10px' }}/>{t('Tournament:lobby.registeredTournament')}</Typography>
                        </Stack>
                    </Box>
                </Box>
            </div>
            {modalOpen && (
                <DetachedTournamentLobby title={`Lobby for: ${modalTournament?.name}`} onClose={handleModalClose}>
                    <TournamentLobbyContent tournament={modalTournament} userState={userState} interactionHandler={interactionHandler}></TournamentLobbyContent>
                </DetachedTournamentLobby>
            )}
        </ThemeProvider>
    );
};

export default DesktopTournamentLobby; 
